import type {
  OosaFlagDetails,
  PatientFlag,
} from '@seen/core/models/flags/patientFlag'
import * as patients from '@seen/core/models/patients'
import {
  differenceInCalendarDays,
  format,
  isSameYear,
  parseISO,
} from 'date-fns'
import { orderBy } from 'lodash-es'
import { escapeHtml } from '#src/components/rich-text-editor/conditionNoteHtml.ts'

export const OOSA_IDT_APPROVAL_MIN_DAYS = 30

export const OOSA_MODAL_OPEN_DELAY_MS = 160

export type OosaFlagOverlay = 'add' | 'remove' | null

export function formatOosaHoverDate(date: string): string {
  return format(parseISO(date), 'M/d/yy')
}

export function formatOosaPillDateRange(
  startDate: string,
  returnDate?: string
): string {
  const start = parseISO(startDate)
  if (!returnDate) {
    return `${format(start, 'M/d')}-?`
  }
  const end = parseISO(returnDate)
  if (isSameYear(start, end)) {
    return `${format(start, 'M/d')}-${format(end, 'M/d')}`
  }
  return `${format(start, 'M/d/yy')}-${format(end, 'M/d/yy')}`
}

export function latestOosaNotes(
  versions: readonly PatientFlag[],
  fallbackFlags: readonly PatientFlag[] = [],
  flagId?: string
): string {
  const candidates = [...versions, ...fallbackFlags].filter((flag) => {
    if (!flagId) {
      return false
    }
    return flag.toFhirResource().id === flagId
  })
  const timed = orderBy(
    candidates,
    [(flag) => flag.toFhirResource().meta?.lastUpdated ?? ''],
    ['desc']
  )
  return timed.find((flag) => flag.oosaNotes)?.oosaNotes ?? ''
}

export function mergeOosaDetails(
  persisted?: OosaFlagDetails,
  fallback?: OosaFlagDetails
): OosaFlagDetails | undefined {
  const startDate = fallback?.startDate ?? persisted?.startDate
  const returnDateUnknown =
    fallback != null
      ? Boolean(fallback.returnDateUnknown)
      : Boolean(persisted?.returnDateUnknown)
  const returnDate =
    fallback != null ? fallback.returnDate : persisted?.returnDate
  const reason = fallback?.reason ?? persisted?.reason
  if (!startDate || !reason) {
    return undefined
  }
  if (!returnDateUnknown && !returnDate) {
    return undefined
  }
  const source = fallback ?? persisted
  const otherReason =
    reason === patients.OOSA_REASON.OTHER
      ? (source?.otherReason ?? '').trim()
      : ''
  const merged: OosaFlagDetails = {
    startDate,
    ...(returnDateUnknown
      ? { returnDateUnknown: true as const }
      : { returnDate }),
    reason,
    // A just-saved payload is passed as fallback and must win over the
    // still-stale search Flag, including when the user cleared the note.
    notes: fallback != null ? fallback.notes : (persisted?.notes ?? ''),
  }
  if (otherReason) {
    merged.otherReason = otherReason
  }
  return merged
}

export function applyOosaFlagOverlay(
  flagCodes: readonly patients.ParticipantFlagCode[],
  overlay: OosaFlagOverlay
): patients.ParticipantFlagCode[] {
  if (overlay === 'add') {
    return flagCodes.includes(patients.ParticipantFlagCode.OOSA)
      ? [...flagCodes]
      : [...flagCodes, patients.ParticipantFlagCode.OOSA]
  }
  if (overlay === 'remove') {
    return flagCodes.filter(
      (code) => code !== patients.ParticipantFlagCode.OOSA
    )
  }
  return [...flagCodes]
}

export function inclusiveOosaDayCount(
  startDate: string,
  returnDate: string
): number | undefined {
  const start = parseISO(startDate)
  const end = parseISO(returnDate)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return undefined
  }
  return differenceInCalendarDays(end, start) + 1
}

export function isOosaIdtApprovalRequired(
  startDate: string,
  returnDate: string
): boolean {
  const days = inclusiveOosaDayCount(startDate, returnDate)
  return days != null && days >= OOSA_IDT_APPROVAL_MIN_DAYS
}

export function oosaIdtApprovalTaskTitle(participantName: string): string {
  return `Get OOSA IDT approval for ${participantName}`
}

export function oosaInvoluntaryDisenrollmentTaskTitle(
  participantName: string
): string {
  return `Get OOSA IDT decision for involuntary disenrollment for ${participantName}`
}

export type OosaTimelineEventAction = 'set on' | 'modified' | 'off'

export function oosaTimelineEventMessage(
  details: OosaFlagDetails | undefined,
  action: OosaTimelineEventAction
): string {
  // One <p> so TipTap does not leave a last-paragraph gap at the bottom
  // of the timeline card.
  const lines = [`<strong>OOSA ${action}</strong>`]
  if (details) {
    const range = details.returnDateUnknown
      ? `${formatOosaHoverDate(details.startDate)} - Return date unknown`
      : `${formatOosaHoverDate(details.startDate)} - ${formatOosaHoverDate(details.returnDate ?? details.startDate)}`
    const reason = patients.formatOosaReasonDisplay(
      details.reason,
      details.otherReason
    )
    lines.push(escapeHtml(range))
    lines.push(`Reason: ${escapeHtml(reason)}`)
    const notes = details.notes.trim()
    if (notes) {
      lines.push(`Notes: ${escapeHtml(notes).replace(/\r\n|\r|\n/g, '<br>')}`)
    }
  }
  return `<p>${lines.join('<br>')}</p>`
}

export function pinOosaFlagFirst(
  flagCodes: readonly patients.ParticipantFlagCode[]
): patients.ParticipantFlagCode[] {
  const oosa = patients.ParticipantFlagCode.OOSA
  if (!flagCodes.includes(oosa)) {
    return [...flagCodes]
  }
  return [oosa, ...flagCodes.filter((code) => code !== oosa)]
}
